import React, { useEffect, useState } from "react";
import { Languages } from "lucide-react";

/**
 * Google Translate dropdown widget (free, no credits).
 * - Inserts the Google Translate script once
 * - Renders our own custom button that opens the native select
 * - Default page language: English
 */
export default function GoogleTranslate() {
  const [open, setOpen] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (window.__gtInited) {
      setReady(true);
      return;
    }
    window.__gtInited = true;

    window.googleTranslateElementInit = () => {
      // eslint-disable-next-line no-new
      new window.google.translate.TranslateElement(
        {
          pageLanguage: "en",
          includedLanguages: "en,es,fr,it,de,pt,ja,zh-CN",
          layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE,
          autoDisplay: false,
        },
        "google_translate_element"
      );
      setReady(true);
    };

    const s = document.createElement("script");
    s.src = "//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
    s.async = true;
    document.body.appendChild(s);
  }, []);

  const switchTo = (lang) => {
    const select = document.querySelector("select.goog-te-combo");
    if (!select) return;
    select.value = lang;
    select.dispatchEvent(new Event("change"));
    setOpen(false);
  };

  const LANGS = [
    { code: "en", label: "EN", name: "English" },
    { code: "es", label: "ES", name: "Español" },
    { code: "fr", label: "FR", name: "Français" },
    { code: "it", label: "IT", name: "Italiano" },
    { code: "de", label: "DE", name: "Deutsch" },
    { code: "pt", label: "PT", name: "Português" },
  ];

  return (
    <div className="relative" data-testid="translate-widget">
      {/* Hidden native widget (kept off-screen) */}
      <div
        id="google_translate_element"
        style={{ position: "absolute", left: "-9999px", top: "-9999px" }}
        aria-hidden="true"
      />
      <button
        onClick={() => setOpen((o) => !o)}
        disabled={!ready}
        className="flex items-center gap-1.5 px-2.5 py-1 hairline text-silver-300 hover:text-silver-100 transition-colors font-title text-[0.6rem] tracking-[0.24em] uppercase disabled:opacity-50"
        data-testid="translate-toggle"
        aria-label="Change language"
      >
        <Languages size={12} />
        <span className="hidden sm:inline">Lang</span>
      </button>

      {open && (
        <div
          className="fixed right-3 mt-2 min-w-[180px] bg-black hairline z-[60] py-1 shadow-2xl"
          style={{ top: "3.5rem" }}
          data-testid="translate-menu"
        >
          {LANGS.map((l) => (
            <button
              key={l.code}
              onClick={() => switchTo(l.code)}
              className="w-full text-left px-3 py-2 font-title text-[0.65rem] tracking-[0.22em] uppercase text-silver-200 hover:text-white hover:bg-white/5 transition-colors flex items-center justify-between gap-3"
              data-testid={`translate-lang-${l.code}`}
            >
              <span className="text-silver-500">{l.label}</span>
              <span>{l.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
